import { Pool, useToast } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { useCallback, useState, useMemo } from 'react'
import { useAppDispatch } from 'state'
import { updateUserBalance, updateUserPendingReward, updateUserStakedBalance, updateUserAllowance } from 'state/pools'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCatchTxError from 'hooks/useCatchTxError'
import { Token } from '@pancakeswap/sdk'
import BigNumber from 'bignumber.js'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { getDecimalAmount } from '@pancakeswap/utils/formatBalance'
import { useApprovePool, useCheckVaultApprovalStatus, useVaultApprove } from 'views/Pools/hooks/useApprove'
import { useERC20 } from 'hooks/useContract'
import { usePool } from 'state/pools/hooks'

import useStakePool from '../../hooks/useStakePool'
import useUnstakePool from '../../hooks/useUnstakePool'

const StakeModalContainer = ({
  isBnbPool,
  pool,
  isRemovingStake,
  onDismiss,
  stakingTokenBalance,
  stakingTokenPrice,
}: Pool.StakeModalPropsType<Token>) => {
  const { t } = useTranslation()

  const {
    sousId,
    earningToken,
    stakingToken,
    earningTokenPrice,
    apr,
    userData,
    stakingLimit,
    enableEmergencyWithdraw,
  } = pool
  const { account, chainId } = useActiveWeb3React()
  const { toastSuccess } = useToast()
  const { pool: singlePool } = usePool(sousId)
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()
  const [amount, setAmount] = useState('')

  const { onUnstake } = useUnstakePool(sousId, enableEmergencyWithdraw)
  const { onStake } = useStakePool(sousId, isBnbPool)
  const dispatch = useAppDispatch()

  const poolAllowance = singlePool.userData?.allowance
  const { allowance, setLastUpdated } = useCheckVaultApprovalStatus(pool.vaultKey, chainId)
  const stakingTokenContract = useERC20(stakingToken.address || '')
  const { handleApprove: handleApproveVault, pendingTx: pendingTxVault } = useVaultApprove(pool.vaultKey, setLastUpdated)
  const { handleApprove: handleApprovePool, pendingTx: pendingTxPool } = useApprovePool(stakingTokenContract, sousId, stakingToken.symbol)

  const handleApprove = pool.vaultKey ? handleApproveVault : handleApprovePool
  const enablePendingTx = pool.vaultKey ? pendingTxVault : pendingTxPool

  const onDone = useCallback(() => {
    dispatch(updateUserStakedBalance({ sousId, account, chainId }))
    dispatch(updateUserPendingReward({ sousId, account, chainId }))
    dispatch(updateUserBalance({ sousId, account, chainId }))
  }, [dispatch, chainId, sousId, account])

  const handleConfirmClick = useCallback(
    async (stakeAmount: string) => {
      const receipt = await fetchWithCatchTxError(() => {
        if (isRemovingStake) {
          return onUnstake(stakeAmount, stakingToken.decimals)
        }
        return onStake(stakeAmount, stakingToken.decimals)
      })
      if (receipt?.status) {
        if (isRemovingStake) {
          toastSuccess(
            `${t('Pulled out')}!`,
            <ToastDescriptionWithTx txHash={receipt.transactionHash}>
              {t('Your %symbol% earnings have also been harvested to your wallet!', {
                symbol: earningToken.symbol,
              })}
            </ToastDescriptionWithTx>,
          )
        } else {
          toastSuccess(
            `${t('Staked')}!`,
            <ToastDescriptionWithTx txHash={receipt.transactionHash}>
              {t('Your %symbol% funds have been staked in the pool!', {
                symbol: stakingToken.symbol,
              })}
            </ToastDescriptionWithTx>,
          )
        }

        onDone?.()
        onDismiss?.()
      }
    },
    [
      fetchWithCatchTxError,
      isRemovingStake,
      onStake,
      stakingToken.decimals,
      stakingToken.symbol,
      onUnstake,
      onDone,
      onDismiss,
      toastSuccess,
      t,
      earningToken.symbol,
    ],
  )

  const needEnable = useMemo(() => {
    if(pool.vaultKey) {
      if (!isRemovingStake && !pendingTx) {
        const stakeAmount = getDecimalAmount(new BigNumber(amount))
        return stakeAmount.gt(allowance)
      }
    } else {
      const stakeAmount = getDecimalAmount(new BigNumber(amount))
      return stakeAmount.gt(poolAllowance)
    }
    return false
  }, [allowance, amount, pendingTx, isRemovingStake])

  const handleEnableApprove = async () => {
    await handleApprove()
    dispatch(updateUserAllowance({ sousId, account, chainId }))
  }

  return (
    <Pool.StakeModal
      enableEmergencyWithdraw={enableEmergencyWithdraw}
      stakingLimit={stakingLimit}
      stakingTokenPrice={stakingTokenPrice}
      earningTokenPrice={earningTokenPrice}
      stakingTokenDecimals={stakingToken.decimals}
      earningTokenSymbol={earningToken.symbol}
      stakingTokenSymbol={stakingToken.symbol}
      stakingTokenAddress={stakingToken.address}
      stakingTokenBalance={stakingTokenBalance}
      apr={apr}
      userDataStakedBalance={userData.stakedBalance}
      userDataStakingTokenBalance={userData.stakingTokenBalance}
      onDismiss={onDismiss}
      pendingTx={pendingTx}
      account={account}
      needEnable={needEnable}
      enablePendingTx={enablePendingTx}
      handleEnableApprove={handleEnableApprove}
      setAmount={setAmount}
      handleConfirmClick={handleConfirmClick}
      isRemovingStake={isRemovingStake}
    />
  )
}

export default StakeModalContainer
